import { BaseTextFieldProps, Chip, TextField } from '@mui/material';
import { KeyboardEvent, SyntheticEvent } from 'react';

export interface InputTagsProps extends BaseTextFieldProps {
  tags: string[];
  limit?: number;
  name?: string;
  onChange?: (eve: SyntheticEvent | null, tags: string[]) => void;
  disabledInput?: boolean;
}

export default function InputTags({
  name,
  tags,
  limit = 50,
  onChange,
  placeholder = 'Enter',
  label,
  disabledInput,
  ...otherProps
}: InputTagsProps) {
  const persistTagOnEnter = (event: SyntheticEvent) => {
    const inputEle = event.target as HTMLInputElement;
    const tagsArray = inputEle.value
      .split(',')
      .map((tag) => tag.trim())
      .filter(
        (tag, index, arr) =>
          tag !== '' && !tags.includes(tag) && arr.indexOf(tag) === index,
      );

    if (tagsArray.length > 0) {
      onChange?.(event, [...tags, ...tagsArray].slice(0, limit));
    }

    inputEle.value = '';
  };

  const persistTagOnDelimiter = (event: SyntheticEvent) => {
    const inputEle = event.target as HTMLInputElement;
    const [value] = inputEle.value.split(',');
    const tag = value.trim();

    if (tag !== '' && !tags.includes(tag)) {
      onChange?.(event, [...tags, tag]);
    }

    inputEle.value = '';
  };

  const onKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ',') {
      persistTagOnDelimiter(event);
      return;
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const inputEle = event.target as HTMLInputElement;
    if (event.key === 'Backspace' && inputEle.value === '') {
      event.preventDefault();
      const newtags = [...tags];
      const lastTag = newtags.pop();
      inputEle.value = lastTag || '';
      onChange?.(null, newtags);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      persistTagOnEnter(event);
      return;
    }
  };

  const handleDelete = (value: string) => {
    const newtags = tags.filter((val) => val !== value);
    onChange?.(null, newtags);
  };

  const isLimitReached = tags.length >= limit;
  const shouldHideInput = isLimitReached || otherProps.disabled;

  return (
    <TextField
      {...otherProps}
      label={label}
      name={name}
      fullWidth
      size={otherProps.size}
      variant="outlined"
      placeholder={isLimitReached ? '' : placeholder}
      inputProps={{
        sx: {
          width: 0,
          minWidth: '30px',
          flexGrow: 1,
          textOverflow: 'ellipsis',
          padding: '7.5px 4px 7.5px 6px',
          visibility: shouldHideInput ? 'hidden' : 'visible',
        },
      }}
      InputProps={{
        disabled: disabledInput,
        sx: {
          display: 'inline-flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          width: '100%',
          borderRadius: '4px',
          padding: otherProps.size === 'small' ? '1.5px' : '9px',
        },
        startAdornment: tags.map((tag, index) => {
          return (
            <Chip
              key={`${tag}-${index}`}
              label={tag}
              size={otherProps.size}
              sx={{ margin: '4px', maxWidth: 'calc(100% - 6px)' }}
              {...(otherProps.disabled
                ? {}
                : { onDelete: () => handleDelete(tag) })}
            />
          );
        }),
      }}
      onKeyUp={onKeyUp}
      onKeyDown={onKeyDown}
      onBlur={persistTagOnEnter}
    />
  );
}
